// OrderDetails.js - Improved Layout
import React, { useEffect, useState } from "react";
import {
  Modal,
  Card,
  Descriptions,
  Table,
  Tag,
  Button,
  message,
  Row,
  Col,
  Timeline,
  Divider,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  PlayCircleOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";

const { Title, Text } = Typography;

function OrderDetails({ visible, orderId, onClose, onStatusUpdate }) {
  const [orderDetails, setOrderDetails] = useState(null);
  const [modal, contextHolder] = Modal.useModal();

  // API hooks
  const [detailsResponse, getOrderDetails] = useApi();
  const [updateResponse, updateOrderStatus] = useApi();
  const [receiptResponse, getReceipt] = useApi();

  useEffect(() => {
    if (visible && orderId) {
      getOrderDetails(null, API_METHODS.GET, `/admin/orders/${orderId}`);
    }
  }, [visible, orderId]);

  useEffect(() => {
    if (receiptResponse.data?.url) {
      window.open(receiptResponse.data.url, "_blank", "noopener,noreferrer");
    }
  }, [receiptResponse]);

  const handleDownloadReceipt = () => {
    getReceipt(null, API_METHODS.GET, `/admin/orders/${orderId}/receipt`);
  };

  useEffect(() => {
    if (detailsResponse.data && !detailsResponse.isLoading) {
      setOrderDetails(detailsResponse.data);
    }
  }, [detailsResponse]);

  useEffect(() => {
    if (updateResponse.data && !updateResponse.isLoading) {
      message.success("Order status updated successfully");
      onStatusUpdate && onStatusUpdate();
    }
  }, [updateResponse]);

  const handleStatusUpdate = (status) => {
    modal.confirm({
      title: "Update Order Status",
      content: `Are you sure you want to mark this order as ${status}?`,
      okText: "Confirm",
      okButtonProps: {
        style: { backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" },
      },
      onOk: () => {
        updateOrderStatus(
          { status },
          API_METHODS.PUT,
          `/admin/orders/${orderId}`
        );
      },
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  const getBeneficiaryDisplay = (beneficiaryInfo) => {
    if (!beneficiaryInfo?.name) return "-";

    return (
      <div>
        <div style={{ fontWeight: "500", color: "#1890ff" }}>
          {beneficiaryInfo.name}
          {beneficiaryInfo.relationship && (
            <Text
              type="secondary"
              style={{ marginLeft: "4px", fontSize: "11px" }}
            >
              ({beneficiaryInfo.relationship})
            </Text>
          )}
        </div>
        <div style={{ fontSize: "11px", color: "#666" }}>
          {beneficiaryInfo.is_saved ? (
            <Tag color="green" size="small">
              Saved
            </Tag>
          ) : (
            <Tag color="blue" size="small">
              Custom
            </Tag>
          )}
        </div>
      </div>
    );
  };

  const getServiceInstanceDisplay = (item) => {
    if (item.service_instance.total_instances === 1) {
      return item.name;
    }

    return (
      <div>
        <div style={{ fontWeight: "500" }}>{item.name}</div>
        <Tag color="purple" size="small" style={{ marginTop: "2px" }}>
          Instance {item.service_instance.current_instance} of{" "}
          {item.service_instance.total_instances}
        </Tag>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const orderItemColumns = [
    {
      title: "Item",
      key: "service_details",
      width: 250,
      render: (record) => (
        <div>
          <div style={{ fontWeight: "500", marginBottom: "4px" }}>
            {record.name || record.addon_type || "—"}
          </div>
          <div>
            {record.booking_kind === "addon" && (
              <Tag color="orange" size="small">Add-on</Tag>
            )}
            {record.booking_kind === "event" && (
              <Tag color="blue" size="small">Event</Tag>
            )}
            {record.booking_kind === "service" && record.type && (
              <Tag color="cyan" size="small">{record.type}</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "price",
      key: "price",
      width: 100,
      align: "right",
      render: (price) => (
        <div style={{ fontWeight: "500" }}>{formatCurrency(price)}</div>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 60,
      align: "right",
      render: (quantity) => quantity || 1,
    },
    {
      title: "Amount",
      key: "amount",
      width: 100,
      align: "right",
      render: (record) => (
        <div style={{ fontWeight: "bold", color: "#f97316", fontSize: "16px" }}>
          {formatCurrency(parseFloat(record.price) * (record.quantity || 1))}
        </div>
      ),
    },
    {
      title: "Special Instructions",
      dataIndex: "special_instructions",
      key: "special_instructions",
      render: (instructions) => (
        <div
          style={{
            maxWidth: "200px",
            fontSize: "12px",
            color: instructions ? "#333" : "#999",
          }}
        >
          {instructions ? (
            <div
              style={{
                backgroundColor: "#fff7e6",
                padding: "6px 8px",
                borderRadius: "4px",
                border: "1px solid #ffd591",
              }}
            >
              {instructions}
            </div>
          ) : (
            <span style={{ fontStyle: "italic" }}>No special instructions</span>
          )}
        </div>
      ),
    },
    {
      title: "Service Date",
      dataIndex: "service_date",
      key: "service_date",
      width: 120,
      render: (date) => (
        <div style={{ textAlign: "center" }}>
          <CalendarOutlined style={{ color: "#1890ff", marginBottom: "4px" }} />
          <div style={{ fontSize: "12px", fontWeight: "500" }}>
            {formatDate(date)}
          </div>
        </div>
      ),
      sorter: (a, b) => new Date(a.service_date) - new Date(b.service_date),
    },
  ];

  if (!orderDetails) {
    return (
      <>
        {contextHolder}
        <Modal
          destroyOnHidden={true}
          title="Order Details"
          open={visible}
          onCancel={onClose}
          footer={null}
          width={1400}
          loading={detailsResponse.isLoading}
        >
          Loading order details...
        </Modal>
      </>
    );
  }

  const { order: orderInfo, items } = orderDetails;

  // Create timeline based on order status
  const getOrderTimeline = () => {
    const timeline = [];

    timeline.push({
      color: "blue",
      icon: <CalendarOutlined />,
      children: (
        <div>
          <div style={{ fontWeight: "500" }}>Order Created</div>
          <div style={{ color: "#666", fontSize: "12px" }}>
            {new Date(orderInfo.created_on).toLocaleString("en-IN")}
          </div>
        </div>
      ),
    });

    if (
      orderInfo.order_status === "payment_completed" ||
      orderInfo.order_status === "confirmed" ||
      orderInfo.order_status === "in_progress" ||
      orderInfo.order_status === "completed"
    ) {
      timeline.push({
        color: "green",
        icon: <DollarOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: "500" }}>Payment Completed</div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Payment verified and processed
            </div>
          </div>
        ),
      });
    }

    if (
      orderInfo.order_status === "confirmed" ||
      orderInfo.order_status === "in_progress" ||
      orderInfo.order_status === "completed"
    ) {
      timeline.push({
        color: "cyan",
        icon: <CheckCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: "500" }}>Order Confirmed</div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Order confirmed by admin
            </div>
          </div>
        ),
      });
    }

    if (
      orderInfo.order_status === "in_progress" ||
      orderInfo.order_status === "completed"
    ) {
      timeline.push({
        color: "purple",
        icon: <PlayCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: "500" }}>Puja In Progress</div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Puja ceremony started
            </div>
          </div>
        ),
      });
    }

    if (orderInfo.order_status === "completed") {
      timeline.push({
        color: "green",
        icon: <CheckCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: "500" }}>Order Completed</div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Puja ceremony completed successfully
            </div>
          </div>
        ),
      });
    }

    return timeline;
  };

  return (
    <>
    {contextHolder}
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <UserOutlined style={{ marginRight: "8px", color: "#f97316" }} />
          Order Details - {orderInfo.order_number}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        orderInfo.payment_status === "completed" && (
          <Button
            key="receipt"
            icon={<DollarOutlined />}
            onClick={handleDownloadReceipt}
            loading={receiptResponse.isLoading}
          >
            Download Receipt
          </Button>
        ),
        orderInfo.order_status === "confirmed" && (
          <Button
            key="complete-direct"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => handleStatusUpdate("completed")}
            loading={updateResponse.isLoading}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          >
            Complete Directly
          </Button>
        ),
        orderInfo.order_status === "in_progress" && (
          <Button
            key="complete"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => handleStatusUpdate("completed")}
            loading={updateResponse.isLoading}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          >
            Mark as Completed
          </Button>
        ),
      ]}
    >
      {/* Top Section - Order Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={6}>
          <Card
            size="small"
            style={{ textAlign: "center", backgroundColor: "#f0f9ff" }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: "#f97316",
                marginBottom: "4px",
              }}
            >
              {formatCurrency(orderInfo.total_amount)}
            </div>
            <div style={{ color: "#666" }}>Total Amount</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card
            size="small"
            style={{ textAlign: "center", backgroundColor: "#f0fff4" }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#1890ff",
                marginBottom: "4px",
              }}
            >
              {new Date(orderInfo.created_on).toLocaleDateString("en-IN")}
            </div>
            <div style={{ color: "#666" }}>Booking Date</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card
            size="small"
            style={{ textAlign: "center", backgroundColor: "#fef3e2" }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#f97316",
                marginBottom: "4px",
              }}
            >
              {new Date(orderInfo.booking_date).toLocaleDateString("en-IN")}
            </div>
            <div style={{ color: "#666" }}>Puja Date</div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card
            size="small"
            style={{ textAlign: "center", backgroundColor: "#fff7e6" }}
          >
            <Tag
              color={
                orderInfo.order_status === "completed"
                  ? "green"
                  : orderInfo.order_status === "in_progress"
                  ? "purple"
                  : orderInfo.order_status === "confirmed"
                  ? "cyan"
                  : orderInfo.order_status === "payment_completed"
                  ? "blue"
                  : "orange"
              }
              style={{ fontSize: "14px", padding: "4px 12px" }}
            >
              {orderInfo.order_status?.toUpperCase()}
            </Tag>
            <div style={{ color: "#666", marginTop: "4px" }}>Order Status</div>
          </Card>
        </Col>
      </Row>

      {/* Order Items Section - Full Width Priority */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <ShoppingOutlined
              style={{ marginRight: "8px", color: "#f97316" }}
            />
            <Title level={5} style={{ margin: 0 }}>
              Order Items ({items?.length || 0} services)
            </Title>
          </div>
        }
        style={{ marginBottom: "24px" }}
      >
        <Table
          columns={orderItemColumns}
          dataSource={items || []}
          pagination={false}
          size="middle"
          rowKey="id"
          scroll={{ x: 800 }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "even-row" : "odd-row"
          }
          summary={(pageData) => {
            const total = pageData.reduce(
              (sum, item) => sum + parseFloat(item.price) * (item.quantity || 1),
              0
            );
            const totalInstances = pageData.length;
            const uniqueBeneficiaries = new Set(
              pageData.map((item) => item.beneficiary_info?.name)
            ).size;

            return (
              <>
              <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                <Table.Summary.Cell index={0}>
                  <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                    🎯 ORDER TOTALS
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1890ff",
                      fontSize: "12px",
                    }}
                  >
                    {uniqueBeneficiaries} Beneficiaries
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#52c41a",
                      fontSize: "12px",
                    }}
                  >
                    {totalInstances} Services
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#f97316",
                      fontSize: "18px",
                    }}
                  >
                    {formatCurrency(total)}
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}></Table.Summary.Cell>
              </Table.Summary.Row>
              {orderInfo.prasad_courier && orderInfo.prasad_price > 0 ? (
                <Table.Summary.Row style={{ backgroundColor: "#fff7ed" }}>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <div style={{ fontSize: "13px", color: "#9a3412" }}>
                      📦 Prasad Courier Charge
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <div style={{ fontWeight: "bold", color: "#9a3412", fontSize: "14px" }}>
                      {formatCurrency(orderInfo.prasad_price)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}></Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null}
              </>
            );
          }}
        />
      </Card>

      {/* Bottom Section - Customer & Progress */}
      <Row gutter={[16, 16]}>
        {/* Customer Information */}
        <Col xs={24} lg={12}>
          <Card title="Customer Information" size="small" style={{ marginBottom: "16px" }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Name">
                <div style={{ fontWeight: "500", fontSize: "16px" }}>
                  {orderInfo.customer_name || `${orderInfo.first_name || ""} ${orderInfo.last_name || ""}`.trim() || "—"}
                </div>
              </Descriptions.Item>
              {orderInfo.gotra && (
                <Descriptions.Item label="Gotra">
                  <Tag color="purple">{orderInfo.gotra}</Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="WhatsApp">
                {orderInfo.whatsapp_number ? (
                  <a href={`https://wa.me/91${orderInfo.whatsapp_number}`} target="_blank" rel="noopener noreferrer">
                    {orderInfo.whatsapp_number}
                  </a>
                ) : orderInfo.phone ? (
                  <a href={`tel:${orderInfo.phone}`}>{orderInfo.phone}</a>
                ) : "Not provided"}
              </Descriptions.Item>
              {orderInfo.slot && (
                <Descriptions.Item label="Time Slot">
                  <Tag color="blue">
                    {{ "05:00": "5:00 AM", "07:30": "7:30 AM", "10:00": "10:00 AM" }[orderInfo.slot] || orderInfo.slot}
                  </Tag>
                </Descriptions.Item>
              )}
              {orderInfo.notes && (
                <Descriptions.Item label="Notes">
                  <div style={{ background: "#fff7e6", padding: "8px", borderRadius: "4px", border: "1px solid #ffd591" }}>
                    {orderInfo.notes}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {orderInfo.prasad_courier ? (
            <Card title="Prasad by Courier" size="small" style={{ borderColor: "#f97316" }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Recipient">{orderInfo.prasad_name}</Descriptions.Item>
                <Descriptions.Item label="Address">
                  <div style={{ fontSize: "13px" }}>
                    {orderInfo.prasad_address_line1}
                    {orderInfo.prasad_address_line2 && `, ${orderInfo.prasad_address_line2}`}
                    {orderInfo.prasad_city && `, ${orderInfo.prasad_city}`}
                    {orderInfo.prasad_state && `, ${orderInfo.prasad_state}`}
                    {orderInfo.prasad_pincode && ` — ${orderInfo.prasad_pincode}`}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Courier Charge">
                  <span style={{ fontWeight: "bold", color: "#f97316" }}>
                    {formatCurrency(orderInfo.prasad_price || 0)}
                  </span>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ) : (
            <Card size="small" style={{ backgroundColor: "#f9f9f9", color: "#999", textAlign: "center" }}>
              No prasad courier requested
            </Card>
          )}
        </Col>

        {/* Order Timeline & Payment */}
        <Col xs={24} lg={12}>
          <Card
            title="Order Progress"
            size="small"
            style={{ marginBottom: "16px" }}
          >
            <Timeline items={getOrderTimeline()} />
          </Card>

          <Card title="Payment Information" size="small">
            <Row gutter={16}>
              <Col
                span={24}
                style={{ textAlign: "center", marginBottom: "16px" }}
              >
                <DollarOutlined
                  style={{
                    fontSize: "24px",
                    color: "#52c41a",
                    marginBottom: "8px",
                  }}
                />
                <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                  {formatCurrency(
                    orderInfo.payment_amount || orderInfo.total_amount
                  )}
                </div>
                <Tag
                  color={
                    orderInfo.payment_status === "completed"
                      ? "green"
                      : "orange"
                  }
                >
                  {orderInfo.payment_status?.toUpperCase()}
                </Tag>
              </Col>
              {orderInfo.razorpay_payment_id && (
                <Col span={24}>
                  <div
                    style={{
                      fontSize: "11px",
                      fontFamily: "monospace",
                      color: "#666",
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      padding: "4px",
                      borderRadius: "4px",
                    }}
                  >
                    {orderInfo.razorpay_payment_id}
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        </Col>
      </Row>

      <style jsx>{`
        .even-row {
          background-color: #fafafa;
        }
        .odd-row {
          background-color: #ffffff;
        }
        .ant-table-summary {
          background-color: #f0f9ff !important;
        }
      `}</style>
    </Modal>
    </>
  );
}

export default OrderDetails;
